import React from 'react';
import { MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from './ui/empty-state';
import { libelle } from '../lib/libelles';

const EMPTY_ARRAY = [];

/**
 * Lieux et zones actifs, en liste à côté de la carte de flotte. Les mêmes
 * zones sont dessinées sur la carte par `ZoneLayer`.
 */
export default function ZonesList({ zones = EMPTY_ARRAY }) {
  const navigate = useNavigate();

  if (zones.length === 0) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <EmptyState
          variant="empty"
          icon={MapPin}
          title="Aucun point d’intérêt actif"
          description="Dépôts, stations et zones à risque s’afficheront ici et sur la carte dès qu’une zone active sera déclarée."
          actionLabel="Gérer les zones"
          onAction={() => navigate('/geo-locations')}
          className="border-0"
        />
      </div>
    );
  }

  return (
    <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
      {zones.map((zone) => (
        <li
          key={zone.id ?? `${zone.latitude},${zone.longitude}`}
          className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-3"
        >
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              // Une zone à risque est un état, donc une teinte.
              zone.type === 'risk_zone' ? 'bg-flame/15 text-flame-ink' : 'bg-primary/10 text-primary'
            }`}
          >
            <MapPin className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{zone.name || 'Zone sans nom'}</p>
            <p className="truncate text-xs text-muted-foreground">
              {[zone.type ? libelle('locationType', zone.type, '') : '', zone.city, zone.country]
                .filter(Boolean)
                .join(' · ') || 'Type non précisé'}
            </p>
            <p className="text-xs tabular-nums text-muted-foreground/70">
              Rayon {Number(zone.radius_meters || 0).toLocaleString('fr-FR')} m
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}
